import { ProviderInventoryItem } from '@/types/msq'
import { Provider, ProviderConfig, ProviderKey } from '@/types/providers'
import { getProviders, addProvider, deleteProvider, modelsRequest } from '@/utils/api'
import { isApiError } from '@/types/api'
import { Commit, Dispatch } from 'vuex'

export interface ProvidersState {
  configs: Record<string, ProviderConfig>
  modelsLists: Record<string, ProviderInventoryItem[]>
  error: string | null
  loading: boolean
}

export const providers = () => ({
  state: <ProvidersState>{
    configs: {},
    modelsLists: {},
    error: null,
    loading: false
  },
  mutations: {
    setProviderConfigs(state: ProvidersState, configs: Record<string, ProviderConfig>) {
      state.configs = configs
    },
    addProviderConfig(state: ProvidersState, { key, config }: { key: string; config: ProviderConfig }) {
      state.configs = { ...state.configs, [key]: config }
    },
    deleteProviderConfig(state: ProvidersState, key: string) {
      const { [key]: _, ...rest } = state.configs
      state.configs = rest
      // Also remove models list for this provider
      const { [key]: __, ...restModels } = state.modelsLists
      state.modelsLists = restModels
    },
    setProvidersError(state: ProvidersState, error: string | null) {
      state.error = error
    },
    setProvidersLoading(state: ProvidersState, loading: boolean) {
      state.loading = loading
    },
    setModelsList(state: ProvidersState, { key, models }: { key: string; models: ProviderInventoryItem[] }) {
      state.modelsLists = { ...state.modelsLists, [key]: models.sort((a, b) => a.id.localeCompare(b.id)) }
    }
  },
  actions: {
    async fetchProviders({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      commit('setProvidersLoading', true)
      commit('setProvidersError', null)

      const response = await getProviders()

      if (isApiError(response)) {
        console.error('Error fetching providers:', response.message)
        commit('setProvidersError', response.message)
        dispatch('updateSnackbar', { text: `Providers Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Providers Error', content: response }, { root: true })
        commit('setProvidersLoading', false)
        return
      }

      if (response.success) {
        const { providers } = response
        commit('setProviderConfigs', providers)
        dispatch(
          'addLogEvent',
          {
            title: 'Providers Fetched',
            content: { providerCount: Object.keys(providers).length }
          },
          { root: true }
        )

        // Fetch models only using providerKey for already configured providers
        for (const providerConfig of Object.values(providers)) {
          const modelsResponse = await modelsRequest({ providerKey: providerConfig.providerKey })

          if (isApiError(modelsResponse)) {
            console.error(`Error fetching models for provider ${providerConfig.providerKey}:`, modelsResponse.message)
            // Clear models list if fetching fails
            commit('setModelsList', { key: providerConfig.providerKey, models: [] })
            continue
          }

          if (Array.isArray(modelsResponse.data)) {
            commit('setModelsList', { key: providerConfig.providerKey, models: modelsResponse.data })
          } else {
            // Clear models list if fetching fails
            commit('setModelsList', { key: providerConfig.providerKey, models: [] })
          }
        }
      } else {
        const errorMsg = 'Failed to fetch providers'
        commit('setProvidersError', errorMsg)
        dispatch('updateSnackbar', { text: errorMsg }, { root: true })
      }

      commit('setProvidersLoading', false)
    },
    async addProvider({ commit, dispatch }: { commit: Commit; dispatch: Function }, providerConfig: ProviderConfig) {
      commit('setProvidersLoading', true)
      commit('setProvidersError', null)

      // API call still needs full config for initial setup
      const response = await addProvider(providerConfig)

      if (isApiError(response)) {
        console.error('Error adding provider:', response.message)
        commit('setProvidersError', response.message)
        dispatch('updateSnackbar', { text: `Add Provider Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Add Provider Error', content: response }, { root: true })
        commit('setProvidersLoading', false)
        return false
      }

      if (response.success) {
        // Store the config locally (API response doesn't return the full config)
        commit('addProviderConfig', { key: providerConfig.providerKey, config: providerConfig })
        dispatch('updateSnackbar', { text: response.message }, { root: true })
        dispatch('addLogEvent', { title: 'Provider Added', content: providerConfig }, { root: true })

        // Fetch models using only the providerKey now that it's configured
        const modelsResponse = await modelsRequest({ providerKey: providerConfig.providerKey })

        if (isApiError(modelsResponse)) {
          console.error(`Error fetching models for provider ${providerConfig.providerKey}:`, modelsResponse.message)
          commit('setModelsList', { key: providerConfig.providerKey, models: [] })
        } else if (Array.isArray(modelsResponse.data)) {
          commit('setModelsList', { key: providerConfig.providerKey, models: modelsResponse.data })
        } else {
          commit('setModelsList', { key: providerConfig.providerKey, models: [] })
        }

        // Refresh model configs to ensure UI is updated
        await dispatch('fetchModelConfigs', null, { root: true })
        dispatch('markConfigResourcesDirty', ['providers', 'models'], { root: true })

        return true
      } else {
        const errorMsg = response?.message || 'Failed to add provider'
        commit('setProvidersError', errorMsg)
        dispatch('updateSnackbar', { text: errorMsg }, { root: true })
        commit('setProvidersLoading', false)
        return false
      }
    },
    async deleteProvider({ commit, dispatch }: { commit: Commit; dispatch: Function }, providerKey: ProviderKey) {
      commit('setProvidersLoading', true)
      commit('setProvidersError', null)

      const response = await deleteProvider(providerKey)

      if (isApiError(response)) {
        console.error('Error deleting provider:', response.message)
        commit('setProvidersError', response.message)
        dispatch('updateSnackbar', { text: `Delete Provider Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Delete Provider Error', content: response }, { root: true })
        commit('setProvidersLoading', false)
        return false
      }

      if (response.success) {
        // Update local state immediately to ensure UI is updated
        commit('deleteProviderConfig', providerKey)
        dispatch('updateSnackbar', { text: response.message }, { root: true })
        dispatch('addLogEvent', { title: 'Provider Deleted', content: providerKey }, { root: true })

        // Refresh model configs since they might reference this provider
        await dispatch('fetchModelConfigs', null, { root: true })
        dispatch('markConfigResourcesDirty', ['providers', 'models'], { root: true })

        commit('setProvidersLoading', false)
        return true
      } else {
        const errorMsg = response?.message || 'Failed to delete provider'
        commit('setProvidersError', errorMsg)
        dispatch('updateSnackbar', { text: errorMsg }, { root: true })
        commit('setProvidersLoading', false)
        return false
      }
    },
    async fetchProviderModels(
      { commit, dispatch }: { commit: Commit; dispatch: Function },
      { providerConfig }: { providerConfig: Partial<ProviderConfig> & { providerKey: ProviderKey } }
    ) {
      // This action might be called during setup (before saving) or after.
      // Pass the potentially partial config (might include unsaved apiKey/url)
      // to modelsRequest, which handles sending only necessary info.
      commit('setProvidersLoading', true)

      const modelsResponse = await modelsRequest(providerConfig)

      if (isApiError(modelsResponse)) {
        console.error(`Error fetching models for provider ${providerConfig.providerKey}:`, modelsResponse.message)
        commit('setModelsList', { key: providerConfig.providerKey, models: [] })
        dispatch('updateSnackbar', { text: `Provider Models Error: ${modelsResponse.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Provider Models Error', content: modelsResponse }, { root: true })
        commit('setProvidersLoading', false)
        return
      }

      if (Array.isArray(modelsResponse.data)) {
        commit('setModelsList', { key: providerConfig.providerKey, models: modelsResponse.data })
        dispatch(
          'addLogEvent',
          {
            title: 'Provider Models Fetched',
            content: {
              providerKey: providerConfig.providerKey,
              modelCount: modelsResponse.data.length
            }
          },
          { root: true }
        )

        // After fetching models for a provider, refresh model configs
        // This ensures that any UI components displaying models are updated
        await dispatch('fetchModelConfigs', null, { root: true })
      } else {
        commit('setModelsList', { key: providerConfig.providerKey, models: [] })
      }

      commit('setProvidersLoading', false)
    }
  },
  getters: {
    providerConfigs: (state: ProvidersState) => state.configs,
    providerConfigsList: (state: ProvidersState) => Object.values(state.configs),
    providerKeys: (state: ProvidersState) => Object.keys(state.configs),
    providersError: (state: ProvidersState) => state.error,
    providersLoading: (state: ProvidersState) => state.loading,
    builtInProviders: () => Object.values(Provider),
    allProviderModels: (state: ProvidersState) => state.modelsLists,
    // New getter to filter embedding models across all providers
    providerEmbeddingModels: (state: ProvidersState) => {
      // Flatten all provider inventory items into a single array
      const allItems = Object.values(state.modelsLists).flat()
      // Filter only model items that have "embed" in their ID (case insensitive)
      return allItems.filter((item) => item.object === 'model' && item.id.toLowerCase().includes('embed'))
    }
  }
})
